import { useEffect, useState } from 'react';
import { useTableSelector } from 'hooks/useTableSelector';

/**
 * Delay before the over-bet amount is folded into the displayed stack — gives
 * the ReturnedBetChips animation time to visibly land in the seat first.
 * Tuned to ReturnedBetChips' burstDuration + flyDuration (~0.6s) + small buffer.
 */
const OVER_BET_RETURN_DELAY_MS = 700;

/**
 * Returns the stack value that should be shown on a seat, accounting for an
 * over-bet (uncalled portion) that the server has flagged for return but not
 * yet folded back into `seat.stack`.
 *
 * The server lags: it reports `overBet` immediately after `predistpot()` but
 * only commits the returned amount to the player's stack after showdown
 * completes. To avoid showing a stale stack during showdown, this hook
 * optimistically adds the over-bet amount once the flying-chip animation has
 * had time to land.
 *
 * When the server eventually broadcasts the corrected stack (and clears
 * `overBet`), the adjustment naturally goes to zero so there's no double-add.
 */
export function useDisplayedStack(
  seatId: number,
  rawStack: number | undefined
): number | undefined {
  const tableOverBet = useTableSelector((table) => table.game.tableOverBet);
  const [showAdjustment, setShowAdjustment] = useState(false);

  const hasOverBetForSeat =
    tableOverBet?.seatId === seatId && (tableOverBet?.bet ?? 0) > 0;

  useEffect(() => {
    if (!hasOverBetForSeat) {
      setShowAdjustment(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowAdjustment(true);
    }, OVER_BET_RETURN_DELAY_MS);

    return () => clearTimeout(timer);
  }, [hasOverBetForSeat]);

  if (rawStack === undefined) return rawStack;
  if (!showAdjustment) return rawStack;

  return Number(rawStack) + (tableOverBet?.bet ?? 0);
}
